import { Component, Input, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { CalibrationSetup, CalibrationEquation, Sensor, SensorReadingType, TimeZoneDisplay, ExportDataCriteria, SensorCalibrationHistory } from '@models';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { SensorService } from '@services/sensor.service';
import { UserService } from '@services/user.service';
import { Charting } from 'app/shared/classes/charting';
import { CommonShared } from 'app/shared/classes/common';
import { SensorMapping } from 'app/shared/classes/sensorMapping';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

@Component({
  standalone: false,
  selector: 'sensor-calibration',
  templateUrl: './sensor-calibration.component.html',
  styleUrls: ['./sensor-calibration.component.scss']
})

export class SensorCalibrationComponent implements OnInit {
  
  @Input() sensor: Sensor;
  @Input() allSensors: Sensor[];
  //@Input() loadingCalibrationHistory: boolean;
  @Input() notifyCalibrationHistoryActiveChanged: number;

  common = new CommonShared();
  mapping = new SensorMapping();
  charting = new Charting();

  calibrationHistoryDisplayed: SensorCalibrationHistory[] = [];
  selectedCalibrationHistory: SensorCalibrationHistory;
  showChartForSelectedCalibrationHistoryValues: boolean = false;

  chartView: any[] = [740,280];
  chartOptions: any = {
    showXAxis: true,
    showYAxis: true,
    gradient: false,
    showLegend: true,
    animations: false,
    showXAxisLabel: false,
    xAxisLabel: 'Date-Time',
    showYAxisLabel: true,
    yAxisLabel:'Value',
    axisFormat: null,
    autoScale: true,
    legendTitle:'Sensors',
    timeline: false,
    colorScheme: {
      domain: [ '#046dd6', '#800080', '#42f55a', '#ffc107', '#fa0f0f' ]
    }
  };

  generalConfig: any = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select Option',
    noResultsFound: 'No options found!',
    searchOnKey: 'name'
  };

  dataTypeConfig: any = {
    displayKey: 'type',
    search: true,
    height: '300px',
    placeholder: 'Select Data Type',
    noResultsFound: 'No options found!',
    searchOnKey: 'type'
  };

  calibration: CalibrationSetup = { startDate: null, endDate: null, selectedReadingType: null, selectedEquation: { id: -1, name: 'No Calibration' }, chartData: [] };

  equationOptions: CalibrationEquation[] = [{ id: -1, name: 'No Calibration' }, { id: 0, name: '1st-Deg Poly (e.g., 2x - 1)'}, { id: 1, name: '2nd-Deg Poly (e.g., 3x^2 + 5x - 1)' }, { id: 2, name: '3rd-Deg Poly (e.g., 4x^3 + 3x^2 + x - 1)' }];
  readingTypeOptions: SensorReadingType[] = [];

  selectedTimeZone: TimeZoneDisplay = TimeZoneDisplay.Local;
  selectedTimeZoneOffset: number = moment().utcOffset();

  onChangeLock: boolean = false;
  loadingReadingTypes: boolean = false;
  username: string = '';


  constructor(private toastr: ToastrService, public _sensorService: SensorService, public _userService: UserService) {
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.loadSavedSettings();
    this.setupCalibrationView();
    //this.getReadingTypesForSensors();
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes['notifyCalibrationHistoryActiveChanged'] != undefined && changes['notifyCalibrationHistoryActiveChanged'].currentValue > 0) {
      var historyId = changes['notifyCalibrationHistoryActiveChanged'].currentValue;
      this.sensor.activeCalibration = this.sensor.sensorCalibrationHistory.find(cal => cal.id == historyId && cal.isActive);
      this.calibration.selectedEquation = (this.sensor.activeCalibration != undefined) ?
                                          this.equationOptions.find(x => x.id == this.sensor.activeCalibration.equationId) : this.equationOptions[0];

      this.updateCalibrationHistoryDropdownList();
      this.getSensorReadings();
    }
  }


  loadSavedSettings() {
    [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(localStorage.getItem('TimeZoneSelected'));
  }


  getReadingTypesForSensors() {
    this.loadingReadingTypes = true;
    
    this._sensorService.getReadingTypesForSensors([this.sensor.id], 'Getting reading types for sensors').subscribe(
      results => {
        if (results != null) {
          this.readingTypeOptions = results;
          this.setupCalibrationView();
          this.loadingReadingTypes = false;
        }
        else {
          this.toastr.warning('No reading types found for this group of sensors');
        }
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor reading types');
      });
  }


  setupCalibrationView() {
    if (this.sensor.sensorReadingTypes == undefined || this.sensor.sensorReadingTypes.length < 1) {
      return;
    }

    this.readingTypeOptions = this.sensor.sensorReadingTypes.filter((v, i, a) => a.indexOf(v) === i);

    // Filter readingTypeOptions so that the types with active calibrations are shown first in dropdown, followed by types without active calibrations

    this.readingTypeOptions.sort((a, b) => {
      var aActiveCal = this.sensor.sensorCalibrationHistory?.find(cal => cal.sensorReadingTypeId == a.id && cal.isActive);  
      var bActiveCal = this.sensor.sensorCalibrationHistory?.find(cal => cal.sensorReadingTypeId == b.id && cal.isActive);
      if (aActiveCal != undefined && bActiveCal == undefined) {
        return -1;
      }
      if (aActiveCal == undefined && bActiveCal != undefined) {
        return 1;
      }
      return 0;
    });

    this.calibration.selectedReadingType = this.readingTypeOptions[0];

    this.calibration.startDate = this.getFormattedDate(moment().local().add(-3, "days").toDate());
    this.calibration.endDate = this.getFormattedDate(moment().local().toDate());

    this.sensor.checked = true;
    this.verifySensorHasCalibrationForEachReadingType(this.sensor);

    this.sensor.activeCalibration = this.sensor.sensorCalibrationHistory.find(cal => cal.sensorReadingTypeId == this.calibration.selectedReadingType.id && cal.isActive);

    this.calibration.selectedEquation = (this.sensor.activeCalibration != undefined) ?
                                          this.equationOptions.find(x => x.id == this.sensor.activeCalibration.equationId) : this.equationOptions[0];
                             
    this.updateCalibrationHistoryDropdownList();
    this.getSensorReadings();
  }


  verifySensorHasCalibrationForEachReadingType(sensor: Sensor) {
    if (sensor.sensorReadingTypes == undefined || sensor.sensorReadingTypes.length < 1) {
      return;
    }

    sensor.sensorCalibrationHistory = (sensor.sensorCalibrationHistory == undefined) ? [] : sensor.sensorCalibrationHistory;

    // Ensure each reading type has a calibration entry, populated from active history if available
    sensor.sensorReadingTypes.forEach(x => {
      var existingCalibration = sensor.sensorCalibrationHistory.find(y => y.sensorReadingTypeId == x.id);

      // Try to load from active calibration history
      var activeHistory = this.sensor.sensorCalibrationHistory?.find(ch => ch.sensorReadingTypeId == x.id && ch.isActive);

      if (existingCalibration != undefined) {
        if (activeHistory) {
          existingCalibration.equationId = activeHistory.equationId;
          existingCalibration.parameters = activeHistory.parameters || [1, 0, 0, 0];
        }
        existingCalibration.checked = true;
        existingCalibration.showChartCalValues = true;
        existingCalibration.showChartRawValues = true;
      }
      else {
        var newCalibration = this.createNewCalibrationHistory(sensor.id, x.id);
        if (activeHistory) {
          newCalibration.equationId = activeHistory.equationId;
          newCalibration.parameters = activeHistory.parameters || [1, 0, 0, 0];
        }
        sensor.sensorCalibrationHistory.push(newCalibration);
      }
    });
  }


  updateCalibrationHistoryDropdownList() {
    if (this.calibration == undefined || this.calibration.selectedReadingType == undefined) {
      return;
    }
    this.calibrationHistoryDisplayed = this.sensor.sensorCalibrationHistory.filter(ch => ch.sensorReadingTypeId == this.calibration.selectedReadingType.id);
  }


  getFormattedDate(date: Date): string {
    const dateFormatted = moment(date).local().format('YYYY-MM-DD HH:mm');
    return dateFormatted;
  }


  createNewCalibrationHistory(sensorId: number, readingTypeId: number): SensorCalibrationHistory {
    var calibration: SensorCalibrationHistory = {
      id: 0,
      sensorId: sensorId,
      sensorName: this.sensor.name,
      sensorReadingTypeId: readingTypeId,
      sensorReadingTypeName: '',
      equationId: -1,
      parameters: [1, 0, 0, 0],
      checked: true,
      showChartCalValues: true,
      showChartRawValues: true,
      notes: '',
      notesObj: { userNotes: '', grade: 0, sensorSerialNumber: '' },
      templateName: '',
      startDate: moment().local().toDate(),
      modifiedDate: moment().local().toDate(),
      isActive: false
    }
    return calibration;
  }


  getSensorReadings() {
    var chartStartDate = this.calibration.startDate;
    var chartEndDate = this.calibration.endDate;

    if (chartStartDate == null || chartEndDate == null) {
      return;
    }

    if (!moment(chartStartDate, "YYYY-MM-DD hh:mm").isValid()) {
      this.toastr.error('Start date is not valid. Use yyyy-mm-dd hh:mm');
      return;
    }

    if (!moment(chartEndDate, "YYYY-MM-DD hh:mm").isValid()) {
      this.toastr.error('End date is not valid. Use yyyy-mm-dd hh:mm');
      return;
    }

    var readingTypes = this.readingTypeOptions.map(x => x.type);
    if (readingTypes == undefined || readingTypes.length < 1) {
      this.toastr.warning('Sensors not associated with reading/datatypes');
      return;
    }

    var dataCriteria: ExportDataCriteria = {
      filename: '',
      saveAs: null,
      startDate: moment(chartStartDate).format('YYYY-MM-DD HH:mm'),
      endDate: moment(chartEndDate).format('YYYY-MM-DD HH:mm'),
      uuids: [this.sensor.uuid],
      sensorReadingTypes: readingTypes,
      interpolateData: null,
      interpolateInterval: null,
      interpolateIntervalValue: null,
      interpolateMethod: null,
      ignoreOutliers: null,
      ignoreOutliersLessThan: null,
      ignoreOutliersGreaterThan: null,
      retrieveRawValues: true,
      includeGpsInfo: false,
      timeZoneDisplay: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset,
      includeOrigValues: true,
      includeCalValues: false
    }

    this._sensorService.exportSensorReadings(dataCriteria).subscribe(
      results => {
        if (results != null) {
          if (results.length == 0) {
            this.toastr.warning('No sensor readings available for the given time period');
          }
          else {
            this.parseSensorReadingsForCalibrationSensors(results);
            this.updateChartReadings();
          }
        } else {
          this.toastr.warning('No sensor readings found to export');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while attempting to export sensor data');
      });
  }


  parseSensorReadingsForCalibrationSensors(sensorReadings: SensorReadingInfo[]) {
    sensorReadings.forEach(x => {
      x.createdDate = moment.utc(x.createdDate).local().toDate();
    });

    var uuids = sensorReadings.map(x => x.uuid).filter((v, i, a) => a.indexOf(v) === i);
    uuids.forEach(x => {
      var sensor = this.sensor;

      if (this.sensor.uuid.toLowerCase() === x.toLowerCase()) {
        var readingsForSensor = sensorReadings.filter(s => s.uuid.toLowerCase() === x.toLowerCase());
        if (readingsForSensor != undefined) {
          sensor.sensorReadingInfos = [];
          readingsForSensor.forEach(y => {
            var sensorReadingInfo: SensorReadingInfo = {
              createdDate: y.createdDate,
              value: y.value,
              origValue: y.origValue,
              uuid: x,
              type: y.type
            }
            sensor.sensorReadingInfos.push(sensorReadingInfo);
          })
        }
      }
    });
  }


  selectedEquationChanged() {
    //var sensors = [this.sensor];  

    if (this.onChangeLock) { return; }

    this.onChangeLock = true;

    //sensors.forEach(sensor => {
      this.sensor.activeCalibration = this.sensor.sensorCalibrationHistory.find(cal => cal.sensorReadingTypeId == this.calibration.selectedReadingType.id && cal.isActive);

      if (this.sensor.activeCalibration == undefined) {
        this.sensor.activeCalibration = this.sensor.sensorCalibrationHistory.find(cal => cal.sensorReadingTypeId == this.calibration.selectedReadingType.id);
      }

      if (this.sensor.activeCalibration != undefined) {
        this.sensor.activeCalibration.equationId = this.calibration.selectedEquation.id;
      }

      // sensor.sensorCalibrationHistory.forEach(cal => {
      //   if (cal.sensorReadingTypeId == this.calibration.selectedReadingType.id) {
      //       cal.equationId = this.calibration.selectedEquation.id;
      //   }
      // }); 
    //});


    this.validateCalibrationValues([this.sensor]);
    this.updateChartReadings();
    this.onChangeLock = false;
  }


  selectedReadingTypeChanged() {
    //var sensors = [this.sensor];
  
    if (this.onChangeLock) { return; }

    this.onChangeLock = true;

    var existingCalibration = this.sensor.sensorCalibrationHistory.find(y => y.sensorReadingTypeId == this.calibration.selectedReadingType.id && y.isActive);

    if (existingCalibration == undefined) {
      existingCalibration = this.sensor.sensorCalibrationHistory.find(y => y.sensorReadingTypeId == this.calibration.selectedReadingType.id);
    }

    if (existingCalibration == undefined) {
      this.onChangeLock = false;
      return;
    }

    this.calibration.selectedEquation = this.equationOptions.find(y => y.id == existingCalibration.equationId);

    //sensors.forEach(sensor => {
      this.sensor.activeCalibration = this.sensor.sensorCalibrationHistory.find(cal => cal.sensorReadingTypeId == this.calibration.selectedReadingType.id && cal.isActive);

      if (this.sensor.activeCalibration == undefined) {
        this.sensor.activeCalibration = this.sensor.sensorCalibrationHistory.find(cal => cal.sensorReadingTypeId == this.calibration.selectedReadingType.id);
      }

      // sensor.sensorCalibrationHistory.forEach(cal => {
      //   if (cal.sensorReadingTypeId == this.calibration.selectedReadingType.id) {
      //       cal.equationId = this.calibration.selectedEquation.id;
      //   }
      // }); 
    //});

    this.validateCalibrationValues([this.sensor]);   
    this.updateChartReadings();

    this.onChangeLock = false;
  }


  sensorParametersChanged(sensor: Sensor) {
    this.onChangeLock = true;

    sensor.activeCalibration = sensor.sensorCalibrationHistory.find(cal => cal.sensorReadingTypeId == this.calibration.selectedReadingType.id && cal.isActive);

    if (sensor.activeCalibration == undefined) {
      sensor.activeCalibration = sensor.sensorCalibrationHistory.find(cal => cal.sensorReadingTypeId == this.calibration.selectedReadingType.id);
    }

    this.validateCalibrationValues([sensor]);
    this.updateChartReadings();
    this.onChangeLock = false;
  }


  validateCalibrationValues(sensors: Sensor[]) {
    sensors.forEach(sensor => {
      if (sensor.sensorCalibrationHistory != undefined) {
        sensor.sensorCalibrationHistory.forEach(calibration => {
          calibration.parameters = (calibration.parameters == undefined) ? [ 1, 0, 0, 0 ] : calibration.parameters;

          while (calibration.parameters.length < 4) {
            calibration.parameters.push(0);
          }
          
          calibration.parameters = this.removeNulls(calibration.parameters);
        });
      }
    });

    this.updateCalibrationHistoryDropdownList();
  }


  removeNulls(parameters: number[]): number[] {
    var newParms: number[] = [];
    parameters.forEach(x => (x == undefined || x == null) ? newParms.push(0) : newParms.push(x));
    return newParms;
  }


  updateChartReadings() {
    this.calibration.chartData = [];

    var sensor = this.sensor;
      var currentCalibration = sensor.sensorCalibrationHistory.find(x => x.sensorReadingTypeId == this.calibration.selectedReadingType.id);

      if (sensor.sensorReadingInfos != undefined && sensor.sensorReadingInfos.length > 0) {

        // Orig-Raw Values

        if (currentCalibration.showChartRawValues && this.calibration.selectedReadingType != undefined) {
          var sensorReadings = sensor.sensorReadingInfos.filter(x => x.type == this.calibration.selectedReadingType.type && x.value != "nan" && x.value != "NaN" )
                                                        .map(a => ({ name: a.createdDate, value: a.value }));

          var addRawSensorReadings = {
            name: this.common.getSensorDisplayName(sensor) + ' (orig)',
            series: sensorReadings,
            uuid: sensor.uuid
          };

          if (addRawSensorReadings.series.length > 0) {
            this.calibration.chartData.push(addRawSensorReadings);
          }
        }

        // Calibrated Values

        if (currentCalibration.showChartCalValues) {
          var calReadings = sensor.sensorReadingInfos.filter(x => x.type == this.calibration.selectedReadingType.type && x.value != "nan" && x.value != "NaN" )
                                                    .map(a => ({ name: a.createdDate, value: this.generateCalibrationValueForSensor(sensor, Number(a.value)).toString() }));

          var addCalSensorReadings = {
            name: this.common.getSensorDisplayName(sensor) + ' (cal)',
            series: calReadings,
            uuid: sensor.uuid
          };

          if (addCalSensorReadings.series.length > 0) {
            this.calibration.chartData.push(addCalSensorReadings);
          }
        }

        // Selected Calibration History Values

        if (this.showChartForSelectedCalibrationHistoryValues && this.selectedCalibrationHistory != undefined) {
          var histCalReadings = sensor.sensorReadingInfos.filter(x => x.type == this.calibration.selectedReadingType.type && x.value != "nan" && x.value != "NaN" )
                                                          .map(a => ({ name: a.createdDate, value: this.generateCalibrationValueForSensorUsingHistory(sensor, Number(a.value)).toString() }));
          var addHistCalSensorReadings = {
            name: this.common.getSensorDisplayName(sensor) + ' (hist)',
            series: histCalReadings,
            uuid: sensor.uuid
          };

          if (addHistCalSensorReadings.series.length > 0) {
            this.calibration.chartData.push(addHistCalSensorReadings);
          }
        }
      }

    this.calibration.chartData = [...this.calibration.chartData];
  }

 
  generateCalibrationValueForSensor(sensor: Sensor, value: number): number {
    var currentCalibration = sensor.sensorCalibrationHistory.find(x => x.sensorReadingTypeId == this.calibration.selectedReadingType.id);
    switch (currentCalibration.equationId) {
      case -1:
        return value;

      case 0:
        return currentCalibration.parameters[0] * value +
               currentCalibration.parameters[1];

      case 1:
        return currentCalibration.parameters[0] * value * value +
               currentCalibration.parameters[1] * value +
               currentCalibration.parameters[2];

      case 2:
        return currentCalibration.parameters[0] * value * value * value +
               currentCalibration.parameters[1] * value * value +
               currentCalibration.parameters[2] * value +
               currentCalibration.parameters[3];

      default:
        return value;
    }
  }


  generateCalibrationValueForSensorUsingHistory(sensor: Sensor, value: number): number {
    switch (this.selectedCalibrationHistory.equationId) {
      case -1:
        return value;

      case 0:
        return this.selectedCalibrationHistory.parameters[0] * value +
               this.selectedCalibrationHistory.parameters[1];

      case 1:
        return this.selectedCalibrationHistory.parameters[0] * value * value +
               this.selectedCalibrationHistory.parameters[1] * value +
               this.selectedCalibrationHistory.parameters[2];

      case 2:
        return this.selectedCalibrationHistory.parameters[0] * value * value * value +
               this.selectedCalibrationHistory.parameters[1] * value * value +
               this.selectedCalibrationHistory.parameters[2] * value +
               this.selectedCalibrationHistory.parameters[3];

      default:
        return value;
    }
  }


  copyCalibrationValues(from: SensorCalibrationHistory, to: SensorCalibrationHistory) {
    to.id = from.id;
    to.equationId = from.equationId;
    to.sensorReadingTypeId = from.sensorReadingTypeId;
    to.parameters = JSON.parse(JSON.stringify(from.parameters));
  }


  resetCalibrationValues(sensorCalibrations: SensorCalibrationHistory) {
    sensorCalibrations.equationId = -1;
    sensorCalibrations.parameters = [ 1, 0, 0, 0 ];
  }


  updateSensorCalibrationParameter(sensor: Sensor, parameterIndex: number, newValue: number) {
    var currentCalibration = sensor.sensorCalibrationHistory.find(x => x.sensorReadingTypeId == this.calibration.selectedReadingType.id);

    if (currentCalibration != undefined && currentCalibration.parameters != undefined && currentCalibration.parameters.length > 0) {
      currentCalibration.parameters[parameterIndex] = newValue;
    }
  }


  getSensorCalibrationParameter(sensor: Sensor, parameterIndex: number): number {
    var currentCalibration = sensor.sensorCalibrationHistory.find(x => x.sensorReadingTypeId == this.calibration.selectedReadingType.id);
    if (currentCalibration != undefined && currentCalibration.parameters != undefined && currentCalibration.parameters.length > 0) {
      return currentCalibration.parameters[parameterIndex];
    }
    return 0;
  }


}
